import {computed, observable } from '@api'
import { Utils } from '@tldraw/core'
import Vec from '@tldraw/vec'
import {TDBinding, TDShape, TDShapeType, TLDR, WhiteboardStore} from './'

export class WhiteboardClipboardStore {
  constructor(public store: WhiteboardStore) {}

  get page() { return this.store.page}
  get selectedIds() {return this.store.selectedIds}
  get readOnly() { return this.store.readOnly}
  get state() { return this.store.state}
  get currentPageId() { return this.store.currentPageId}
  get getShape() { return this.store.getShape}

  @observable clipboard?: {
    shapes: TDShape[]
    bindings: TDBinding[]
  }

  @computed get clipboardBounds() {
    const {clipboard, store: {shapeUtilFor}} = this,
      shapes = (clipboard?.shapes ?? []).filter(s => shapeUtilFor(s) != null)

    return _.isEmpty(shapes) ? null : Utils.getCommonBounds(shapes.map(s => shapeUtilFor(s).getBounds(s)))
  }

  @computed get isClipboardContentsWithinViewport() {
    const {store: {ui: {viewport}}, clipboardBounds} = this
    return !clipboardBounds ? false : Utils.boundsContain(viewport, clipboardBounds)
  }

  @computed get hasClipboardContents() {
    const {clipboard} = this
    return !_.isEmpty(clipboard?.shapes) || !_.isEmpty(clipboard?.bindings)
  }

  @observable pasteInfo = {
    center: [0, 0],
    offset: [0, 0],
  }

  /* -------------------------------------------------- */
  /*                      Clipboard                     */
  /* -------------------------------------------------- */

  /**
   * Copy one or more shapes to the clipboard.
   * @param ids The ids of the shapes to copy.
   */
  copy = (ids = this.selectedIds): this => {
    const copyingShapeIds = ids.flatMap((id) =>
      this.store.getDocumentBranch(id, this.currentPageId)
    )

    const copyingShapes = copyingShapeIds.map((id) =>
      Utils.deepClone(this.getShape(id))
    )

    if (copyingShapes.length === 0) return this

    const copyingBindings: TDBinding[] = Object.values(this.page.bindings).filter(
      (binding) =>
        copyingShapeIds.includes(binding.fromId) && copyingShapeIds.includes(binding.toId)
    )

    this.clipboard = {
      shapes: copyingShapes,
      bindings: copyingBindings,
    }

    try {
      const text = JSON.stringify({
        type: 'tldr/clipboard',
        shapes: copyingShapes,
        bindings: copyingBindings,
      })

      navigator.clipboard.writeText(text).then(
        () => {
          // success
        },
        () => {
          // failure
        }
      )
    } catch (e) {
      // Browser does not support copying to clipboard
    }

    this.pasteInfo.offset = [0, 0]
    this.pasteInfo.center = [0, 0]

    return this
  }

  /**
   * Cut (copy and delete) one or more shapes to the clipboard.
   * @param ids The ids of the shapes to cut.
   */
  cut = (ids = this.selectedIds): this => {
    this.copy(ids)
    this.store.deleteShapes(ids)
    return this
  }

  /**
   * Paste shapes (or text) from clipboard to a certain point.
   * @param point
   */
  paste = (e?: KeyboardEvent, point?: number[]) => {
    if (this.readOnly) return

    const {store: {
      getPagePoint,getTopChildIndex,
      selection: {select}, shapeUtil: {getBounds}, shapeUtilFor,
      ui: {currentStyle, nudgeDistanceLarge, nudgeDistanceSmall}, currentPageId,
      create, createShapes, camera: {rendererCenterPoint}}, isClipboardContentsWithinViewport,
    } = this

    const pasteInCurrentPage = (shapes: TDShape[], bindings: TDBinding[]) => {
      const idsMap: Record<string, string> = {}

      shapes.forEach((shape) => (idsMap[shape.id] = Utils.uniqueId()))

      bindings.forEach((binding) => (idsMap[binding.id] = Utils.uniqueId()))

      let startIndex = getTopChildIndex(this.currentPageId)

      const shapesToPaste = shapes
        .sort((a, b) => a.childIndex - b.childIndex)
        .map((shape) => {
          const parentShapeId = idsMap[shape.parentId]

          const copy = {
            ...shape,
            id: idsMap[shape.id],
            parentId: parentShapeId || this.currentPageId,
          }

          if (shape.children) {
            copy.children = shape.children.map((id) => idsMap[id])
          }

          if (!parentShapeId) {
            copy.childIndex = startIndex
            startIndex++
          }

          if (copy.handles) {
            Object.values(copy.handles).forEach((handle) => {
              if (handle.bindingId) {
                handle.bindingId = idsMap[handle.bindingId]
              }
            })
          }

          return copy
        })

      const bindingsToPaste = bindings.map((binding) => ({
        ...binding,
        id: idsMap[binding.id],
        toId: idsMap[binding.toId],
        fromId: idsMap[binding.fromId],
      }))

      let delta : number[]

      if (!isClipboardContentsWithinViewport) {
        const commonBounds = Utils.getCommonBounds(shapesToPaste.map(getBounds))

        let center = Vec.toFixed(getPagePoint(point || rendererCenterPoint))

        if (
          Vec.dist(center, this.pasteInfo.center) < 2 ||
          Vec.dist(center, Vec.toFixed(Utils.getBoundsCenter(commonBounds))) < 2
        ) {
          center = Vec.add(center, this.pasteInfo.offset)
          this.pasteInfo.offset = Vec.add(this.pasteInfo.offset, [
            nudgeDistanceLarge,
            nudgeDistanceLarge,
          ])
        } else {
          this.pasteInfo.center = center
          this.pasteInfo.offset = [0, 0]
        }

        const centeredBounds = Utils.centerBounds(commonBounds, center)

        delta = Vec.sub(
          Utils.getBoundsCenter(centeredBounds),
          Utils.getBoundsCenter(commonBounds)
        )
      }
      else {
        delta = [nudgeDistanceLarge, nudgeDistanceLarge]
      }

      create(
        shapesToPaste.map((shape) =>
          shapeUtilFor(shape.type).create({
            ...shape,
            point: delta ? Vec.toFixed(Vec.add(shape.point, delta)) : _.cloneDeep(shape.point),
            parentId: shape.parentId || this.currentPageId,
          })
        ),
        bindingsToPaste
      )
    }
    try {
      if (!('clipboard' in navigator && navigator.clipboard.readText)) {
        throw Error('This browser does not support the clipboard API.')
      }

      navigator.clipboard.readText().then((result) => {
        try {
          const data: { type: string; shapes: TDShape[]; bindings: TDBinding[] } =
            JSON.parse(result)

          if (data.type !== 'tldr/clipboard') {
            throw Error('The pasted string was not from the Tldraw clipboard.')
          }

          pasteInCurrentPage(data.shapes, data.bindings)
        } catch (e) {
          console.warn(e)

          const shapeId = Utils.uniqueId()

          createShapes({
            id: shapeId,
            type: 'text',
            parentId: currentPageId,
            text: TLDR.normalizeText(result),
            point: getPagePoint(rendererCenterPoint, currentPageId),
            style: currentStyle
          })

          select(shapeId)
        }
      })
    } catch (e) {
      // Navigator does not support clipboard. Note that this fallback will
      // not support pasting from one document to another.
      if (this.clipboard) {
        pasteInCurrentPage(this.clipboard.shapes, this.clipboard.bindings)
      }
    }

    return this
  }

  /**
   * Copy one or more shapes as SVG.
   * @param ids The ids of the shapes to copy.
   * @param pageId The page from which to copy the shapes.
   * @returns A string containing the JSON.
   */
  copySvg = (ids = this.selectedIds, pageId = this.currentPageId) => {
    if (ids.length === 0) ids = Object.keys(this.page.shapes)
    if (ids.length === 0) return

    const {store: {shapeUtilFor, shapeUtil: {getRotatedBounds}}, getShape} = this,
      shapes = ids.map((id) => getShape(id, pageId)),
      commonBounds = Utils.getCommonBounds(shapes.map(getRotatedBounds)),
      padding = 16

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    const style = document.createElementNS('http://www.w3.org/2000/svg', 'style')

    style.textContent = `@import url('https://fonts.googleapis.com/css2?family=Caveat+Brush&family=Source+Code+Pro&family=Source+Sans+Pro&family=Source+Serif+Pro&display=swap');`
    defs.appendChild(style)
    svg.appendChild(defs)

    function getSvgElementForShape(shape: TDShape) {
      const util = shapeUtilFor(shape)
      const element = util.getSvgElement(shape)
      const bounds = util.getBounds(shape)

      if (!element) return

      element.setAttribute(
        'transform',
        `translate(${padding + shape.point[0] - commonBounds.minX}, ${
          padding + shape.point[1] - commonBounds.minY
        }) rotate(${((shape.rotation || 0) * 180) / Math.PI}, ${bounds.width / 2}, ${
          bounds.height / 2
        })`
      )

      return element
    }

    shapes.forEach((shape) => {
      if (shape.children?.length) {
        // Create a group <g> element for shape
        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')

        // Get the shape's children as elements
        shape.children
          .map((childId) => this.getShape(childId, pageId))
          .map(getSvgElementForShape)
          .filter(Boolean)
          .forEach((element) => g.appendChild(element!))

        // Add the group element to the SVG
        svg.appendChild(g)

        return
      }

      const element = getSvgElementForShape(shape)

      if (element) {
        svg.appendChild(element)
      }
    })

    // Resize the element to the bounding box
    svg.setAttribute(
      'viewBox',
      [0, 0, commonBounds.width + padding * 2, commonBounds.height + padding * 2].join(' ')
    )

    svg.setAttribute('width', String(commonBounds.width))
    svg.setAttribute('height', String(commonBounds.height))

    const s = new XMLSerializer()

    const svgString = s
      .serializeToString(svg)
      .replaceAll('&#10;      ', '')
      .replaceAll(/((\s|")[0-9]*\.[0-9]{2})([0-9]*)(\b|"|\))/g, '$1')

    TLDR.copyStringToClipboard(svgString)

    return svgString
  }

  /**
   * Copy one or more shapes as JSON.
   * @param ids The ids of the shapes to copy.
   * @param pageId The page from which to copy the shapes.
   * @returns A string containing the JSON.
   */
  copyJson = (ids = this.selectedIds, pageId = this.currentPageId) => {
    if (ids.length === 0) ids = Object.keys(this.page.shapes)
    if (ids.length === 0) return

    const shapes = ids.map((id) => this.getShape(id, pageId))
    const json = JSON.stringify(shapes, null, 2)
    TLDR.copyStringToClipboard(json)
    return json
  }
}


// WEBPACK FOOTER //
// ./src/modules/whiteboard/WhiteboardClipboardStore.ts